import React, { useEffect, useMemo } from "react";
import { Checkbox } from "../ui/checkbox";
import { t } from "../../locales";
import { PermissionType, UserBranchFormData } from "../../types/users";
import {
  getGujaratiPermissionModule,
  getGujaratiPermissionAction,
  sortPermissionsByModuleAndAction,
} from "../../static/permission";

type BranchItem = {
  id: number;
  name: string;
  code: string;
  address: string | null;
};

type Props = {
  branches: BranchItem[];
  permissions: PermissionType[];
  value: UserBranchFormData[];
  onChange: (value: UserBranchFormData[]) => void;
};

const BranchPermissionSections = ({
  branches,
  permissions,
  value,
  onChange,
}: Props) => {
  const permissionsByModule = useMemo(
    () => sortPermissionsByModuleAndAction(permissions),
    [permissions]
  );

  // Initialize branches that don't have data yet
  useEffect(() => {
    if (branches.length > 0 && permissions.length > 0) {
      const existing = new Set(value.map((v) => v.branchId));
      const missing = branches.filter((b) => !existing.has(b.id));
      if (missing.length > 0) {
        const newEntries: UserBranchFormData[] = missing.map((b) => ({
          branchId: b.id,
          permissions: [],
        }));
        onChange([...value, ...newEntries]);
      }
    }
  }, [branches, permissions]);

  const getBranchData = (branchId: number): UserBranchFormData => {
    return (
      value.find((v) => v.branchId === branchId) || {
        branchId,
        permissions: [],
      }
    );
  };

  const updateBranch = (
    branchId: number,
    updates: Partial<UserBranchFormData>
  ) => {
    const newValue = value.map((v) =>
      v.branchId === branchId ? { ...v, ...updates } : v
    );
    onChange(newValue);
  };

  const handlePermissionToggle = (branchId: number, permId: number) => {
    const data = getBranchData(branchId);
    const newPerms = data.permissions.includes(permId)
      ? data.permissions.filter((id) => id !== permId)
      : [...data.permissions, permId];
    updateBranch(branchId, { permissions: newPerms });
  };

  const handleSelectAllModule = (
    branchId: number,
    modulePerms: PermissionType[],
    checked: boolean
  ) => {
    const data = getBranchData(branchId);
    const modulePermIds = modulePerms.map((p) => p.id);
    let newPerms: number[];

    if (checked) {
      const toAdd = modulePermIds.filter(
        (id) => !data.permissions.includes(id)
      );
      newPerms = [...data.permissions, ...toAdd];
    } else {
      newPerms = data.permissions.filter(
        (id) => !modulePermIds.includes(id)
      );
    }
    updateBranch(branchId, { permissions: newPerms });
  };

  const isModuleAllSelected = (
    branchId: number,
    modulePerms: PermissionType[]
  ) => {
    const data = getBranchData(branchId);
    return modulePerms.every((p) => data.permissions.includes(p.id));
  };

  if (branches.length === 0) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-[#113F67]">
        {t.users.branchPermissions}
      </h3>

      {branches.map((branch) => {
        const data = getBranchData(branch.id);
        return (
          <div key={branch.id}>
            <div className="px-4 py-3 border-b border-gray-200">
              <span className="text-base font-semibold text-[#113F67]">
                {branch.name} ({branch.code})
              </span>
            </div>

            <div className="py-4 space-y-4">
                {Object.entries(permissionsByModule).map(
                  ([module, modulePerms]) => {
                    const allSelected = isModuleAllSelected(
                      branch.id,
                      modulePerms
                    );
                    return (
                      <div
                        key={module}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-[#113F67] capitalize">
                            {getGujaratiPermissionModule(module)}
                          </span>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={(checked) =>
                                handleSelectAllModule(
                                  branch.id,
                                  modulePerms,
                                  !!checked
                                )
                              }
                            />
                            <span className="text-sm text-gray-500">
                              {t.users.selectAllModule}
                            </span>
                          </div>
                        </div>
                        {!(
                          modulePerms.length === 1 && !modulePerms[0].action
                        ) && (
                          <div className="grid grid-cols-2 gap-3">
                            {modulePerms.map((perm) => {
                              const isChecked = data.permissions.includes(
                                perm.id
                              );
                              return (
                                <div
                                  key={perm.id}
                                  className="flex items-center gap-2"
                                >
                                  <Checkbox
                                    checked={isChecked}
                                    onCheckedChange={() =>
                                      handlePermissionToggle(
                                        branch.id,
                                        perm.id
                                      )
                                    }
                                  />
                                  <span className="text-sm text-gray-700">
                                    {perm.action
                                      ? getGujaratiPermissionAction(perm.action)
                                      : perm.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BranchPermissionSections;
